// Copyright (c) 2016 Deere & Company

import React, {PropTypes} from 'react';
import {
    getRepairedDateErrorMessage,
    getVsrMessage,
    hasVsrErrorForClaimField,
    isRepairDateAfterFailureDate,
    isResponsibleAccountEditable
} from '../../../shared/services/claim-shared-helper';
import AttachmentClaim from '@deere/cps-it.warranty-model-factory/lib/claim-attachment';
import ClaimAction from '@deere/cps-it.warranty-model-factory/lib/claim-action';
import ClaimRgaStatusIndicator from '../../../shared/components/claim-rga-status-indicator';
import Dealer from '@deere/cps-it.warranty-model-factory/lib/dealer';
import FormValidation from '../../state/form-validation';
import {FormattedMessage} from 'react-intl';
import KeyValuePair from '@deere/cps-it.warranty-model-factory/lib/key-value-pair';
import LabeledDateInput from '../../../shared/components/claim-fields/labeled-date-input';
import LabeledInput from '../../../shared/components/claim-fields/labeled-input';
import LabeledInputWithBlur from '../../../shared/components/claim-fields/labeled-input-with-blur';
import LabeledKeyValueDropDown from '../../../shared/components/claim-fields/labeled-key-value-dropdown';
import ResponsibleAccounts from '../../../shared/components/claim-fields/responsible-accounts';
import {isValidClaim} from '../../services/claim-validation-helper';
import {isValidDate} from '../../../../shared/services/build-date';
import moment from 'moment';

function handleItemNumberBlur(itemNumber) {

    this.actions.updateAttachmentClaimInputOnBlur('itemNumber', itemNumber);

    if (itemNumber) {

        this.actions.validateItemNumber(itemNumber);

    }

}

function handleInputBlur(property, value) {

    this.actions.updateAttachmentClaimInputOnBlur(property, value);

}

function handleResponsibleAccountChange(accountId) {

    this.actions.updateResponsibleAccountsDropDown(accountId, this.selectedAccounts);

}

function handleWorkOrderOneChange(workOrder) {

    this.actions.updateAttachmentClaimWorkOrderOne(workOrder);

}

function handleFailurePartChange(partNumber) {

    this.actions.updateAttachmentClaimFailurePart(partNumber);

}

function handleFailureDateChange(date) {

    this.actions.updateAttachmentClaimFailureDate(date);

}

function handleRepairDateChange(date) {

    this.actions.updateAttachmentClaimRepairDate(date);

}

function handleButtonClick() {

    const action = new ClaimAction({
        actionName: 'VALIDATE',
        id: 'VALIDATE',
        mode: ''
    });

    this.actions.updateSelectedAction(action);

}

function handleOwnershipStatusChange(event) {

    this.actions.updateOwnershipStatusDropDown(event.target.value, this.ownershipStatus);

}

function handleDropDownBlur(property, event) {

    this.actions.updateAttachmentClaimInputOnBlur(property, event.target.value);

}

function handleWorkOrderTwoChange(workOrder) {

    this.actions.updateAttachmentClaimWorkOrderTwo(workOrder);

}

function handleRepairNumberChange(repairNumber) {

    this.actions.updateAttachmentClaimRepairNumber(repairNumber);

}

function handleClaimPropertyChange(property, value) {

    this.actions.updateAttachmentClaimProperty(property, value);

}

function handleDateBlur(property, date) {

    const modifiedDate = isValidDate(date.target.value) ? date.target.value : null;

    this.actions.updateAttachmentClaimInputOnBlur(property, modifiedDate);

}

const ClaimDetailsContainer = props => {

    const canEditResponsibleAccount = isResponsibleAccountEditable(props.isEditable, props.claim.claimStatus);

    return (
        <section className='claim-details-section'>
            <div className='claim-details-section-header'>
                <h3>
                    <FormattedMessage id='PRODUCT_INFORMATION'/>
                </h3>
                {props.claim.rgaStatus.status && props.claim.rgaStatus.status !== 'NONE' &&
                    <ClaimRgaStatusIndicator
                        claim={props.claim.claimNumber}
                        claimRgaStatus={props.claim.rgaStatus}
                    />
                }
            </div>
            <div className='product-info'>
                <ResponsibleAccounts
                    dealers={props.selectedAccounts}
                    errorMessage='DEALER_LOCATION_IS_REQUIRED'
                    highlightClass={hasVsrErrorForClaimField(props.claim.errors, 'responsibleAccount', canEditResponsibleAccount)}
                    isEditable={canEditResponsibleAccount}
                    isValid={props.formValidation.responsibleAccount}
                    onOptionBlur={handleInputBlur.bind(props, 'responsibleAccount')}
                    onOptionChange={props::handleResponsibleAccountChange}
                    selectedAccount={props.claim.responsibleAccount}
                    vsrMessage={getVsrMessage(props.claim.errors, 'responsibleAccount', canEditResponsibleAccount)}
                />
                <LabeledInputWithBlur
                    className='item-number'
                    errorMessage='ITEM_NUMBER_IS_REQUIRED'
                    highlightClass={hasVsrErrorForClaimField(props.claim.errors, 'itemNumber', canEditResponsibleAccount)}
                    isEditable={props.isEditable}
                    isValid={props.formValidation.itemNumber}
                    label='ITEM_NUMBER'
                    maxLength={40}
                    onBlur={props::handleItemNumberBlur}
                    onChange={handleClaimPropertyChange.bind(props, 'itemNumber')}
                    value={props.claim.itemNumber}
                    vsrMessage={getVsrMessage(props.claim.errors, 'itemNumber', canEditResponsibleAccount)}
                />
            </div>
            <h3>
                <FormattedMessage id='REFERENCE_INFORMATION'/>
            </h3>
            <div className='reference-info'>
                <LabeledInputWithBlur
                    className='work-order-one'
                    errorMessage='WORK_ORDER_ONE_IS_REQUIRED'
                    highlightClass={hasVsrErrorForClaimField(props.claim.errors, 'workOrderOne', props.isEditable)}
                    isEditable={props.isEditable}
                    isValid={props.formValidation.workOrderOne}
                    label='WORK_ORDER_ONE'
                    maxLength={26}
                    onBlur={handleInputBlur.bind(props, 'workOrderOne')}
                    onChange={props::handleWorkOrderOneChange}
                    value={props.claim.referenceInfo.referenceNumber1}
                    vsrMessage={getVsrMessage(props.claim.errors, 'workOrderOne', props.isEditable)}
                />
                <LabeledInput
                    className='work-order-two'
                    highlightClass={hasVsrErrorForClaimField(props.claim.errors, 'workOrderTwo', props.isEditable)}
                    isEditable={props.isEditable}
                    label='WORK_ORDER_TWO'
                    maxLength={12}
                    onChange={props::handleWorkOrderTwoChange}
                    value={props.claim.referenceInfo.referenceNumber2}
                    vsrMessage={getVsrMessage(props.claim.errors, 'workOrderTwo', props.isEditable)}
                />
                <LabeledInput
                    className='repair-number'
                    highlightClass={hasVsrErrorForClaimField(props.claim.errors, 'repairNumber', props.isEditable)}
                    isEditable={props.isEditable}
                    label='REPAIR_NUMBER'
                    maxLength={13}
                    onChange={props::handleRepairNumberChange}
                    value={props.claim.referenceInfo.repairNumber}
                    vsrMessage={getVsrMessage(props.claim.errors, 'repairNumber', props.isEditable)}
                />
                <LabeledKeyValueDropDown
                    className='ownership-status'
                    errorMessage='OWNERSHIP_STATUS_IS_REQUIRED'
                    highlightClass={hasVsrErrorForClaimField(props.claim.errors, 'ownershipStatus', props.isEditable)}
                    isEditable={props.isEditable}
                    isValid={props.formValidation.ownershipStatus}
                    label='OWNERSHIP_STATUS'
                    list={props.ownershipStatus}
                    onBlur={handleDropDownBlur.bind(props, 'ownershipStatus')}
                    onChange={props::handleOwnershipStatusChange}
                    selectedValue={props.claim.referenceInfo.ownershipStatus}
                    vsrMessage={getVsrMessage(props.claim.errors, 'ownershipStatus', props.isEditable)}
                />
            </div>
            <div className='replacement-fields'>
                <LabeledInputWithBlur
                    className='previous-invoice-number'
                    errorMessage='PREVIOUS_INVOICE_NUMBER_IS_REQUIRED'
                    highlightClass={hasVsrErrorForClaimField(props.claim.errors, 'previousInvoiceNumber', props.isEditable)}
                    isEditable={props.isEditable}
                    isValid={props.formValidation.previousInvoiceNumber}
                    label='PREVIOUS_INVOICE_NUMBER'
                    maxLength={15}
                    onBlur={handleInputBlur.bind(props, 'previousInvoiceNumber')}
                    onChange={handleClaimPropertyChange.bind(props, 'previousInvoiceNumber')}
                    value={props.claim.previousInvoiceNumber}
                    vsrMessage={getVsrMessage(props.claim.errors, 'previousInvoiceNumber', props.isEditable)}
                />
                <LabeledDateInput
                    className='retail-install-date'
                    date={props.claim.retailInstallDate}
                    errorMessage='RETAIL_INSTALL_IS_REQUIRED'
                    highlightClass={hasVsrErrorForClaimField(props.claim.errors, 'retailInstallDate', props.isEditable)}
                    isEditable={props.isEditable}
                    isValid={props.formValidation.retailInstallDate}
                    label='RETAIL_INSTALL'
                    maxDate={moment.utc(props.claim.failureInfo.failureDate)}
                    onBlur={handleDateBlur.bind(props, 'retailInstallDate')}
                    onDateChange={handleClaimPropertyChange.bind(props, 'retailInstallDate')}
                    vsrMessage={getVsrMessage(props.claim.errors, 'retailInstallDate', props.isEditable)}
                />
            </div>
            <div className='failure-info'>
                <LabeledDateInput
                    className='failure-date'
                    date={props.claim.failureInfo.failureDate}
                    errorMessage='FAILED_DATE_IS_REQUIRED'
                    highlightClass={hasVsrErrorForClaimField(props.claim.errors, 'failureDate', props.isEditable)}
                    isEditable={props.isEditable}
                    isValid={props.formValidation.failureDate}
                    label='FAILED'
                    maxDate={moment()}
                    onBlur={handleDateBlur.bind(props, 'failureDate')}
                    onDateChange={props::handleFailureDateChange}
                    vsrMessage={getVsrMessage(props.claim.errors, 'failureDate', props.isEditable)}
                />
                <LabeledDateInput
                    className='repair-date'
                    date={props.claim.failureInfo.repairDate}
                    errorMessage={getRepairedDateErrorMessage(props.claim.failureInfo.repairDate)}
                    highlightClass={hasVsrErrorForClaimField(props.claim.errors, 'repairDate', props.isEditable)}
                    isEditable={props.isEditable}
                    isValid={props.formValidation.repairDate && isRepairDateAfterFailureDate(props.claim.failureInfo.repairDate, props.claim.failureInfo.failureDate)}
                    label='REPAIRED'
                    maxDate={moment()}
                    onBlur={handleDateBlur.bind(props, 'repairDate')}
                    onDateChange={props::handleRepairDateChange}
                    vsrMessage={getVsrMessage(props.claim.errors, 'repairDate', props.isEditable)}
                />
                <LabeledInputWithBlur
                    className='failure-part'
                    errorMessage='FAILED_PART_IS_REQUIRED'
                    highlightClass={hasVsrErrorForClaimField(props.claim.errors, 'failurePart', props.isEditable)}
                    isEditable={props.isEditable}
                    isValid={props.formValidation.failurePart}
                    label='FAILED_PART'
                    maxLength={18}
                    onBlur={handleInputBlur.bind(props, 'failurePart')}
                    onChange={props::handleFailurePartChange}
                    value={props.claim.failureInfo.failurePartNumber}
                    vsrMessage={getVsrMessage(props.claim.errors, 'failurePart', props.isEditable)}
                />
            </div>
            {(props.isEditable && props.claim.showValidate) &&
                <div className='button-group'>
                    <button
                        disabled={!isValidClaim(props.claim)}
                        onClick={props::handleButtonClick}
                    >
                        <FormattedMessage id='VALIDATE'/>
                    </button>
                </div>
            }
        </section>
    );

};

ClaimDetailsContainer.displayName = 'ClaimDetailsContainer';
ClaimDetailsContainer.propTypes = {
    actions: PropTypes.object.isRequired,
    claim: PropTypes.instanceOf(AttachmentClaim).isRequired,
    formValidation: PropTypes.instanceOf(FormValidation).isRequired,
    isEditable: PropTypes.bool.isRequired,
    isValidClaim: PropTypes.bool.isRequired,
    ownershipStatus: PropTypes.arrayOf(PropTypes.instanceOf(KeyValuePair)).isRequired,
    selectedAccounts: PropTypes.arrayOf(PropTypes.instanceOf(Dealer)).isRequired
};

export default ClaimDetailsContainer;



// WEBPACK FOOTER //
// ./src/claim/attachment/components/claim-details-section/claim-details-container.js